import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/utils/api';
import { showError } from '@/utils/sweetalert';

export interface OrderItem {
  id?: string;
  productId: string;
  variantId?: string;
  name: string;
  price: number;
  quantity: number;
  color?: string;
  size?: string;
  image: string;
  currency?: string;
}

interface OrderContextValue {
  items: OrderItem[];
  addItem: (item: OrderItem) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clearOrder: () => void;
  totalItems: number;
  subtotal: number;
  orderId: string;
  createdAt: string;
  status: string;
  statusLabel: string;
  isLoading: boolean;
}

const OrderContext = createContext<OrderContextValue | null>(null);

export function OrderProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<OrderItem[]>([]);
  const [subtotal, setSubtotal] = useState(0);
  const [orderId, setOrderId] = useState<string>('ORD-GUEST');
  const [isLoading, setIsLoading] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!user?.id) {
      setItems([]);
      setSubtotal(0);
      return;
    }
    setIsLoading(true);
    try {
      const data = await api.get<any>('/orders/current');
      if (data && data.items) {
        setItems(data.items.map((i: any) => ({
          id: i.id,
          productId: i.productId,
          variantId: i.variantId || undefined,
          name: i.productName,
          price: Number(i.price),
          quantity: i.quantity,
          image: i.imageUrl || '',
          color: i.variantName ? i.variantName.split(' - ')[0] : undefined, // Approximation for UI backward compatibility
          size: i.variantName && i.variantName.includes(' - ') ? i.variantName.split(' - ')[1] : undefined,
        })));
        setSubtotal(Number(data.subtotal));
        setOrderId(data.reference);
      } else {
        setItems([]);
        setSubtotal(0);
      }
    } catch (err) {
      console.error('Failed to fetch current order', err);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const addItem = useCallback(async (item: OrderItem) => {
    if (!user?.id) {
      await showError('Connexion requise', 'Veuillez vous connecter pour ajouter des articles.');
      return;
    }
    try {
      await api.post('/orders/current/items', {
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity,
      });
      await fetchOrder();
    } catch (err: any) {
      await showError("Impossible d'ajouter  l'article", err?.message || 'Une erreur est survenue.');
    }
  }, [user?.id, fetchOrder]);

  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      await removeItem(itemId);
      return;
    }
    try {
      await api.patch(`/orders/current/items/${itemId}`, { quantity });
      await fetchOrder();
    } catch (err: any) {
      await showError('Erreur lors de la mise à jour de la quantité', err?.message || 'Une erreur est survenue.');
    }
  }, [fetchOrder]);

  const removeItem = useCallback(async (itemId: string) => {
    try {
      await api.delete(`/orders/current/items/${itemId}`);
      await fetchOrder();
    } catch (err: any) {
      await showError('Erreur lors de la suppression de l\'article', err?.message || 'Une erreur est survenue.');
    }
  }, [fetchOrder]);

  const clearOrder = useCallback(() => {
    setItems([]);
    setSubtotal(0);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <OrderContext.Provider
      value={{
        items,
        addItem,
        updateQuantity,
        removeItem,
        clearOrder,
        totalItems,
        subtotal,
        orderId,
        createdAt: new Date().toISOString(),
        status: 'open',
        statusLabel: 'Commande ouverte',
        isLoading,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
}

export function useOrder(): OrderContextValue {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error('useOrder must be used within OrderProvider');
  return ctx;
}
